import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLoanDocuments, setSelectedLoans, setSelectedBorrowers, setShowSystemValues, getAllLoans, getLoansForTypeahead, getLoanIdsForTypeahead, getRecords, setCombinedData } from '../store/slices/databaseSlice';
import { analyzeDocuments, clearResults, clearLoanResults, savePreviewResults } from '../store/slices/documentsSlice';
import { useFilter } from '../contexts/FilterContext';
import { useAuth } from '../contexts/AuthContext';
import AvailableDocuments from './AvailableDocuments';
import FileViewer from './FileViewer';
import ScansPanelContent from './actionMenu/ScansPanelContent';
import InstructionsPanelContent from './actionMenu/InstructionsPanelContent';
import ExtractionPreview from './ExtractionPreview';
import DocumentSelectionModal from './DocumentSelectionModal';
import api from '../services/api';
import { addNotification } from '../store/slices/uiSlice';
import { colors } from '../theme/colors';
import { apiFetch } from '../utils/apiUtils';
import { 
  CheckSquare, 
  BarChart3, 
  Shield, 
  FolderOpen, 
  Zap, 
  Layout,
  History,
  BarChart2,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Upload,
  Clock,
  Trash2,
  X,
  Database,
  Columns,
  Tags,
  Filter,
  Grid3X3,
  ToolCase,
  Settings2,
  Plus,
  Power,
  PowerOff,
  Sparkles,
  FileCheck,
  FileX,
  FileClock,
  FileText,
  BriefcaseMedical,
  RefreshCw,
  Users
} from 'lucide-react';
import ExportButton from './ExportButton';
import FilterButton, { FilterButtonGroup } from './FilterButton';
import MultiSelectTypeahead from './MultiSelectTypeahead';
import DocumentMappingModal from './DocumentMappingModal';
import FieldMatchModal from './FieldMatchModal';
import PanelButton from './PanelButton';
import SummaryContent from './actionMenu/SummaryContent';
import UploadContent from './actionMenu/UploadContent';
import FieldsContent from './actionMenu/FieldsContent';
import UploadsContent from './actionMenu/UploadsContent';
import ActivityContent from './actionMenu/ActivityContent';
import CaseStatusContent from './actionMenu/CaseStatusContent';
import DocumentsContent from './actionMenu/DocumentsContent';
import InstructionsContent from './actionMenu/InstructionsContent';
import ImportSection from './actionMenu/ImportSection';
import AddFieldSection from './actionMenu/AddFieldSection';
import SystemFilesContent from './actionMenu/SystemFilesContent';
import CaseListContent from './actionMenu/CaseListContent';
import AdminContent from './actionMenu/AdminContent';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const Sidebar = styled.div`
  width: 100px;
  background: #1a1a1a;
  border-right: 1px solid #2d2d2d;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 73px;
  height: calc(100vh - 73px);
  z-index: 9; /* Lower z-index to go behind modals */
  
  /* Dark scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #4a4a4a #2a2a2a;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
`;

const ContentPanel = styled.div`
  width: 320px;
  background: #2d2d2d; /* Always gray background */
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0; /* Never shrink */
  position: fixed;
  left: 100px; /* Start after the sidebar */
  top: 73px;
  height: calc(100vh - 73px);
  z-index: 1; /* Slide behind the sidebar */
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-300px)'}; /* Slide behind sidebar, leaving 20px peeking out */
  transition: transform 0.3s ease;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  display: flex;
  flex-direction: column;
  
  /* Dark scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #4a4a4a #3a3a3a;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #3a3a3a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
`;

const MenuHeader = styled.div`
  padding: 1rem 0.5rem;
  border-bottom: 1px solid #2d2d2d;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MenuTitle = styled.div`
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
`;

const MenuContent = styled.div`
  flex: 1;
  padding: 0 0 0.5rem 0;
`;


const FieldControlButton = styled.button`
  padding: 0.625rem 1rem;
  background: #4a4a4a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  
  &:hover {
    background: #4b5563;
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FieldControlRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FieldControlSection = styled.div`
  margin-bottom: 2rem; /* 32px - Material Design spacing for distinct content sections */
`;

const FieldControlExplainer = styled.div`
  color: #a0a0a0;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const FieldInstructions = styled.div`
  margin-bottom: 1.5rem;
  
  p {
    color: #a0a0a0;
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const FieldSection = styled.div`
  margin-bottom: 2rem; /* 32px - Material Design spacing for distinct content sections */
  
  h4 {
    color: #e0e0e0;
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    font-weight: 600;
  }
`;


const PriorityDisplay = styled.div`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
`;

const PriorityTimeline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DocumentSquare = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  cursor: help;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: ${props => {
    switch (props.type) {
      case 'note':
        return '#2563eb'; // Blue
      case 'proposal':
        return '#059669'; // Green
      case 'service_agreement':
        return '#d97706'; // Orange
      case 'other':
      case 'additional':
        return '#7c3aed'; // Purple
      default:
        return '#6b7280'; // Gray
    }
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:hover::after {
    content: '${props => props.fullName}';
    position: absolute;
    top: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: #4a4a4a;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #4a4a4a;
    z-index: 1000;
  }
`;

const TimelineArrow = styled.div`
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: #4a4a4a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #4b5563;
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SidebarItem = styled.button`
  width: 100%;
  padding: 1.25rem 0.25rem;
  background: ${props => props.isActive ? '#2d2d2d' : 'transparent'};
  border: none;
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  position: relative;
  opacity: ${props => props.isDisabled ? 0.5 : 1};

  &:first-of-type {
    padding-top: 2.25rem;
  }

  &:hover {
    background: ${props => props.isDisabled ? 'transparent' : (props.isActive ? '#2d2d2d' : '#1a1a1a')};
  }
`;

const SidebarIcon = styled.div`
  color: ${props => props.isDisabled ? '#666666' : (props.isActive ? '#3b82f6' : '#ffffff')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const SidebarLabel = styled.span`
  color: ${props => props.isDisabled ? '#666666' : '#ffffff'};
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.1;
`;


const ContentHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  background: transparent;
`;

const ContentTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #f0f0f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ContentIcon = styled.div`
  color: ${props => props.color || '#a0a0a0'};
  display: flex;
  align-items: center;
`;

const ContentBody = styled.div`
  padding: 2rem 1.5rem 1.5rem 1.5rem; /* 32px top, 24px sides/bottom - Material Design spacing */
  flex: 1;
  background-color: #2d2d2d;
  overflow-x: hidden;
  max-width: 100%;
`;

const PlaceholderContent = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  padding: 2rem;
  font-style: italic;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
`;

const SummaryMetrics = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
`;

const MetricCard = styled.div`
  flex: 1;
  text-align: center;
  padding: 0.375rem;
  background: #3a3a3a;
  border-radius: 4px;
`;

const MetricValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.125rem;
`;

const MetricLabel = styled.div`
  font-size: 0.6rem;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  white-space: nowrap;
`;

const Divider = styled.div`
  height: 1px;
  background: #666;
  margin: 1.5rem 0 1rem 0;
  opacity: 0.8;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #555;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #2d2d2d;
  color: #f0f0f0;
  
  &::placeholder {
    color: #a0a0a0;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0a0a0;
  display: flex;
  align-items: center;
`;


const SectionDivider = styled.div`
  height: 1px;
  background: #666;
  margin: 1.5rem 0;
  opacity: 0.8;
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 50%;
  left: ${props => props.isCollapsed ? '100px' : '420px'}; /* Touch the sidebar when collapsed, or full width when expanded */
  transform: translateY(-50%);
  width: 24px;
  height: 48px;
  background: #2d2d2d;
  border: none; /* Removed border */
  border-radius: 0 6px 6px 0;
  color: #f0f0f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: left 0.3s ease;
  z-index: 1000; /* Higher z-index to appear above other elements */
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #f0f0f0;
  margin: 0 0 0.75rem 0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ActivityItem = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 4px;
  border-left: 3px solid #3b82f6;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;



/**
 * PANEL CONTENT COMPONENT - Left sidebar with dark gray background
 * Contains Summary, Upload, Tasks, etc. tabs
 * This is the "Panel Content" area - NOT the main white content area
 */
function ActionMenu({ loanId, loanData, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // loanId is already passed from Layout which extracts it from URL params
  // No need to use useParams again here
  
  // Since we want all sections always available, set this to true
  const isDocumentAnalysis = true;
  
  // Initialize activeSection based on URL parameters
  const urlParams = new URLSearchParams(location.search);
  const initialSection = location.pathname.includes('/system-files') ? 
    (urlParams.get('tab') === 'add-data' ? 'upload' :
     urlParams.get('tab') === 'fields' ? 'fields' : 'core-data') 
    : location.pathname === '/admin' || location.pathname === '/users' ? 'admin'
    : location.pathname === '/tasks' ? 'tasks'
    : location.pathname === '/case-list' || location.pathname === '/database' ? 'case-list'
    : (location.pathname.includes('/document-analysis') || location.pathname.includes('/history')) ? 'case-list' // Also check for document-analysis and history on initial load
    : 'tasks';
  
  const [activeSection, setActiveSection] = useState(initialSection);
  const [directoryPath, setDirectoryPath] = useState('');
  const [autoPopulateExpanded, setAutoPopulateExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [systemSummary, setSystemSummary] = useState(null);
  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState(false);
  const [latestRecords, setLatestRecords] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [showGlobalPriorityModal, setShowGlobalPriorityModal] = useState(false);
  const [fieldImportText, setFieldImportText] = useState('');
  const [importingFields, setImportingFields] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  
  // Get data directly from Redux store
  const combinedData = useSelector(state => state.database.combinedData || []);
  const allLoans = useSelector(state => state.database.allLoans || []);
  const loansForTypeahead = useSelector(state => state.database.loansForTypeahead || []);
  const loanIdsForTypeahead = useSelector(state => state.database.loanIdsForTypeahead || []);
  const records = useSelector(state => state.database.records || []);
  const selectedLoans = useSelector(state => state.database.selectedLoans || []);
  const selectedBorrowers = useSelector(state => state.database.selectedBorrowers || []);
  const showSystemValues = useSelector(state => state.database.showSystemValues || false);
  const documentsState = useSelector(state => state.documents || {});
  const databaseLoading = useSelector(state => state.database.loading || {});
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  const [showAddFieldSection, setShowAddFieldSection] = useState(false);
  const [showExtractionPreview, setShowExtractionPreview] = useState(false);
  const [showDocumentSelection, setShowDocumentSelection] = useState(false);
  const [selectedDocumentsForExtraction, setSelectedDocumentsForExtraction] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [addingField, setAddingField] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  // Start empty to avoid masking issues with hardcoded fallbacks; will be set from API
  const [defaultDocumentPriority, setDefaultDocumentPriority] = useState([]);
  const hasLoadedDocumentPriority = useRef(false);
  
  // Case Status Management
  const [caseStatus, setCaseStatus] = useState('open'); // 'open', 'closed', 'under_review'
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showCaseStatusPanel, setShowCaseStatusPanel] = useState(false);
  const [showScansPanel, setShowScansPanel] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('open');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusComment, setStatusComment] = useState('');
  
  // Check if we're in create mode
  const isCreateMode = location.pathname === '/create';
  
  // Calculate if content panel should be open
  const isContentPanelOpen = !!activeSection && !isManuallyCollapsed;
  
  // Load default document priority on mount
  useEffect(() => {
    // Load default document priority when on document-related pages or fields page
    if ((location.pathname.includes('/document-analysis') || location.pathname.includes('/system-files')) && !hasLoadedDocumentPriority.current) {
      hasLoadedDocumentPriority.current = true;
      const loadDefaultDocumentPriority = async () => {
        try {
          const response = await apiFetch('/system-files/default-document-priority');
          // apiFetch already returns parsed JSON
          const data = response;
          if (data.success && data.priority_order) {
            setDefaultDocumentPriority(data.priority_order);
          }
        } catch (error) {
          console.error('❌ Error loading default document priority:', error);
          // Keep the fallback default if API fails
        }
      };

      loadDefaultDocumentPriority();
    }
  }, [location.pathname]);

  // Listen for document priority refresh events
  useEffect(() => {
    const handleRefreshDocumentPriority = async () => {
      try {
        const response = await apiFetch('/system-files/default-document-priority');
        // apiFetch already returns parsed JSON
        const data = response;
        if (data.success && data.priority_order) {
          setDefaultDocumentPriority(data.priority_order);
        }
      } catch (error) {
        console.error('❌ Error refreshing default document priority:', error);
      }
    };

    window.addEventListener('refreshDocumentPriority', handleRefreshDocumentPriority);
    return () => window.removeEventListener('refreshDocumentPriority', handleRefreshDocumentPriority);
  }, []);
  
  // Reset the ref when pathname changes
  useEffect(() => {
    hasLoadedDocumentPriority.current = false;
  }, [location.pathname]);


  // Helper function to convert document names to internal keys
  const getDocumentKey = (documentName) => {
    switch (documentName.toLowerCase()) {
      case 'note':
        return 'note';
      case 'proposal':
        return 'proposal';
      case 'service agreement':
        return 'service_agreement';
      case 'additional':
      case 'other':
        return 'additional';
      default:
        return documentName.toLowerCase().replace(/\s+/g, '_');
    }
  };

  // Helper function to get document letter
  const getDocumentLetter = (documentName) => {
    switch (documentName.toLowerCase()) {
      case 'note':
        return 'N';
      case 'proposal':
        return 'P';
      case 'service agreement':
        return 'S';
      case 'additional':
      case 'other':
        return 'A';
      default:
        return documentName.charAt(0).toUpperCase();
    }
  };
  
  
  // Notify parent component when collapse state changes
  useEffect(() => {
    if (onToggle) {
      onToggle(isContentPanelOpen);
    }
  }, [isContentPanelOpen, onToggle]);
  
  const dispatch = useDispatch();
  const { searchQuery, setSearchQuery, activeFilter, setActiveFilter } = useFilter();

  // Callback to handle filtered loans
  const handleFilteredLoansChange = (loanIds) => {
    // This will trigger the filtering in DatabaseViewer
    dispatch(setSelectedLoans(loanIds || []));
  };

  // Load case list data when Cases tab is active
  useEffect(() => {
    if (activeSection === 'case-list' && (loansForTypeahead.length === 0 || loanIdsForTypeahead.length === 0)) {
      // console.log('🔄 Loading case list data...');
      Promise.all([
        dispatch(getLoansForTypeahead()),
        dispatch(getLoanIdsForTypeahead())
      ]).then((results) => {
        // console.log('✅ Case list data loaded:', {
        //   borrowers: results[0].payload?.data?.length || 0,
        //   loanIds: results[1].payload?.data?.length || 0
        // });
      });
    }
  }, [activeSection, loansForTypeahead.length, loanIdsForTypeahead.length, dispatch]);
  
  // Fetch system summary data and latest records
  // Update activeSection when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const newSection = location.pathname.includes('/system-files') ? 
      (urlParams.get('tab') === 'add-data' ? 'core-data' : // Keep core-data highlighted for add-data
       urlParams.get('tab') === 'fields' ? 'fields' : 'core-data') 
      : (location.pathname === '/database' || location.pathname === '/case-list') ? 'case-list'
      : location.pathname === '/tasks' ? 'tasks'
      : (location.pathname === '/admin' || location.pathname === '/users') ? 'admin'
      : (location.pathname.includes('/document-analysis') || location.pathname.includes('/history')) ? 'case-list' // Stay on case-list when viewing a loan or history
      : 'tasks';
    setActiveSection(newSection);
  }, [location]);

  useEffect(() => {
    // Only fetch system data when on system-related pages
    if (location.pathname.includes('/system-files')) {
      const fetchSystemData = async () => {
        try {
          const [summaryResponse, recordsResponse] = await Promise.all([
            api.get('/system-files/summary'),
            // Get unified records for typeahead (same as SystemFiles page)
            // apiFetch already returns parsed JSON
            apiFetch('/system-files/records/unified?limit=5000')
          ]);
          setSystemSummary(summaryResponse.data);
          // Use the loans array from unified endpoint
          setLatestRecords(recordsResponse.loans || []);
        } catch (error) {
          console.error('Error fetching system data:', error);
        }
      };
      
      fetchSystemData();
    }
  }, [location.pathname]);
  
  // Get loan documents from Redux store
  const { loanDocuments, errors: databaseErrors } = useSelector(state => state.database);
  
  // Dynamically group documents by fileType
  const documentsByType = useMemo(() => {
    const grouped = {};
    loanDocuments.forEach(doc => {
      if (doc.fileType) {
        if (!grouped[doc.fileType]) {
          grouped[doc.fileType] = [];
        }
        grouped[doc.fileType].push(doc);
      }
    });
    return grouped;
  }, [loanDocuments]);
  
  const hasAvailableDocuments = loanDocuments.length > 0;
  
  
  // Document preview handler
  const handlePreviewDocument = (document) => {
    setPreviewFile(document);
    setShowPreview(true);
  };

  // Toggle mute handler
  const handleToggleMute = async (fileName, muted) => {
    try {
      // Find the file ID from the documents
      const file = loanDocuments.find(doc => 
        doc.fileName === fileName || doc.original_filename === fileName
      );
      
      if (!file || !file.id) {
        console.warn('File not found for muting:', fileName);
        return;
      }

      await api.put(`/database/toggle-ignored/${loanId}/${file.id}?ignored=${muted}`);
      
      dispatch(addNotification({
        type: 'success',
        message: `✅ File ${muted ? 'muted' : 'unmuted'} successfully!`,
        duration: 3000
      }));
      
      // Refresh loan documents after toggling mute
      dispatch(getLoanDocuments({ loanId, source: 'ActionMenu.jsx:handleToggleMute' }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to ${muted ? 'mute' : 'unmute'} file: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
        duration: 5000
      }));
    }
  };

  // Auto-populate handler
  const handleAutoPopulate = async () => {
    if (!directoryPath.trim()) return;
    
    try {
      await api.autoPopulateFiles(directoryPath);
      
      dispatch(addNotification({
        type: 'success',
        message: '✅ Files auto-populated successfully!',
        duration: 3000
      }));
      
      // Refresh loan documents after auto-populating
      if (loanId) {
        dispatch(getLoanDocuments({ loanId, source: 'ActionMenu.jsx:707' }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `❌ Auto-populate failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
        duration: 5000
      }));
    }
  };

  // Document analysis handlers
  const handleAnalyzeDocuments = () => {
    const files = {
      masterFile: documentsState.masterFile,
      proposalFile: documentsState.proposalFile,
      noteFile: documentsState.noteFile,
      securityFile: documentsState.securityFile
    };
    dispatch(analyzeDocuments(files));
  };

  const handleClearResults = () => {
    dispatch(clearResults());
  };

  const handlePermanentlyDeleteResults = async (loanId) => {
    try {
      const result = await dispatch(clearLoanResults(loanId));
      if (clearLoanResults.fulfilled.match(result)) {
        dispatch(addNotification({
          type: 'success',
          message: `Successfully deleted ${result.payload.deleted_count} extraction results for loan ${loanId}`
        }));
        // Also clear the frontend state
        dispatch(clearResults());
      } else {
        dispatch(addNotification({
          type: 'error',
          message: result.payload || 'Failed to delete extraction results'
        }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to delete extraction results'
      }));
    }
  };
  
  // Get data from Redux store (same as ResultsGrid)
  const extractionResults = documentsState.extractionResults || {};
  
  // Apply overrides (same logic as ResultsGrid)
  const overrides = useSelector(state => state.overrides.overrides);
  const applyOverridesToResults = React.useMemo(() => {
    const overriddenResults = { ...extractionResults };
    
    // Apply any overrides
    Object.keys(overrides).forEach(fieldName => {
      if (overriddenResults[fieldName]) {
        overriddenResults[fieldName] = {
          ...overriddenResults[fieldName],
          ...overrides[fieldName]
        };
      }
    });
    
    return overriddenResults;
  }, [extractionResults, overrides]);
  
  // Filter out metadata from the results and use overridden results (same as ResultsGrid)
  const fieldResults = Object.entries(applyOverridesToResults).filter(([key]) => key !== '_metadata');
  
  // Get metrics from loanData or calculate from Redux data
  const metrics = loanData?.metrics || {
    totalFields: fieldResults.length,
    highMatches: fieldResults.filter(([, data]) => data && (data.system_document_match === 'HIGH' || data.system_document_match === 'PERFECT_MATCH')).length,
    discrepancies: fieldResults.filter(([, data]) => 
      data && data.system_document_match === 'NO_MATCH' && 
      data.master_value !== 'Not Found' && 
      data.value !== 'Not Found'
    ).length,
    matchRate: fieldResults.length > 0 ? Math.round((fieldResults.filter(([, data]) => data && (data.system_document_match === 'HIGH' || data.system_document_match === 'PERFECT_MATCH')).length / fieldResults.length) * 100) : 0
  };

  const selectSection = (sectionKey) => {
    const isExpanding = activeSection !== sectionKey;
    setActiveSection(sectionKey);
    
    // Handle Case List navigation
    if (sectionKey === 'case-list') {
      navigate('/case-list');
      return;
    }
    
    // Handle Tasks navigation
    if (sectionKey === 'tasks') {
      navigate('/tasks');
      return;
    }
    
    // Handle Core Data navigation
    if (sectionKey === 'core-data') {
      navigate('/system-files');
      return;
    }
    
    // Handle Fields navigation
    if (sectionKey === 'fields') {
      navigate('/system-files?tab=fields');
      return;
    }
    
    // Handle Admin navigation
    if (sectionKey === 'admin') {
      navigate('/admin');
      return;
    }
    
    // Update URL for System Files tabs
    if (location.pathname.includes('/system-files')) {
      if (sectionKey === 'upload') {
      navigate('/system-files?tab=add-data');
      } else if (sectionKey === 'fields') {
        navigate('/system-files?tab=fields');
      } else if (sectionKey === 'summary') {
      navigate('/system-files');
      }
    }
    
    // Notify parent about expansion state
    if (onToggle) {
      onToggle(isExpanding);
    }
  };

  // CSV Export function
  const handleGlobalPriorityModalClose = () => {
    setShowGlobalPriorityModal(false);
  };

  const handleGlobalPriorityModalSave = async (settings) => {
    try {
      
      // Convert the document priority array to document names
      const priorityOrder = settings.documentPriority || defaultDocumentPriority;
      
      const response = await apiFetch('/system-files/default-document-priority', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority_order: priorityOrder })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save default document priority');
      }
      
      // Update local state
      setDefaultDocumentPriority(priorityOrder);
      
      // Trigger refresh of document priority display
      window.dispatchEvent(new CustomEvent('refreshDocumentPriority'));
      
      dispatch(addNotification({
        type: 'success',
        message: '✅ Default document priority updated successfully!',
        duration: 3000
      }));
      
    } catch (error) {
      console.error('❌ Error saving global document priority:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to save priority settings: ${error.message}`,
        duration: 5000
      }));
    } finally {
      setShowGlobalPriorityModal(false);
    }
  };

  const handleImportFields = async () => {
    if (!fieldImportText.trim()) return;
    
    setImportingFields(true);
    try {
      const fieldNames = fieldImportText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && !line.startsWith('•') && !line.startsWith('-'))
        .filter(line => line.length > 2); // Remove very short lines
      
      if (fieldNames.length === 0) {
        throw new Error('No valid field names found');
      }

      
      // Call Claude to match field names to existing fields
      const response = await apiFetch('/system-files/fields/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_names: fieldNames })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to match field names');
      }
      
      // Show confirmation modal with results
      setMatchResults(data);
      setShowMatchModal(true);
      
    } catch (error) {
      console.error('❌ Error importing fields:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Import failed: ${error.message}`,
        duration: 5000
      }));
    } finally {
      setImportingFields(false);
    }
  };

  const handleConfirmMatches = async (confirmedMatches, confirmedNewFields) => {
    try {
      
      const response = await apiFetch('/system-files/fields/apply-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          confirmed_matches: confirmedMatches,
          confirmed_new_fields: confirmedNewFields
        })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to apply field matches');
      }
      
      dispatch(addNotification({
        type: 'success',
        message: `🎯 Successfully enabled ${data.enabled_fields?.length || 0} fields! Check the Fields tab to review.`,
        duration: 5000
      }));
      
      
      // Reset panel to default state
      setShowImportSection(false);
      setShowMatchModal(false);
      setMatchResults(null);
      setFieldImportText('');
      
    } catch (error) {
      console.error('❌ Error applying field matches:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to apply field matches: ${error.message}`,
        duration: 5000
      }));
    }
  };

  const handleCloseMatchModal = () => {
    setShowMatchModal(false);
    setMatchResults(null);
  };

  const handleAddSimpleField = async () => {
    if (!newFieldName.trim()) return;
    
    setAddingField(true);
    try {
      const response = await apiFetch('/system-files/fields/add-simple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_name: newFieldName.trim() })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to add field');
      }
      
      dispatch(addNotification({
        type: 'success',
        message: `✅ Field "${newFieldName.trim()}" added successfully!`,
        duration: 3000
      }));
      
      // Reset form and close section
      setNewFieldName('');
      setShowAddFieldSection(false);
      
    } catch (error) {
      console.error('❌ Error adding field:', error);
      dispatch(addNotification({
        type: 'error',
        message: '❌ Failed to add field. Please try again.',
        duration: 3000
      }));
    } finally {
      setAddingField(false);
    }
  };

  const handleEnableAllFields = async () => {
    try {
      setBulkUpdating(true);
      
      const response = await apiFetch('/system-files/fields/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'enable_all' })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to enable all fields');
      }
      
      dispatch(addNotification({
        type: 'success',
        message: `🚀 Successfully enabled ${data.updated_count || 0} fields for matching!`,
        duration: 5000
      }));
      
      
    } catch (error) {
      console.error('❌ Error enabling all fields:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to enable all fields: ${error.message}`,
        duration: 5000
      }));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleDisableAllFields = async () => {
    try {
      setBulkUpdating(true);
      
      const response = await apiFetch('/system-files/fields/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'disable_all' })
      });
      
      // apiFetch already returns parsed JSON
      const data = response;
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to disable all fields');
      }
      
      dispatch(addNotification({
        type: 'success',
        message: `🔒 Successfully disabled ${data.updated_count || 0} fields from matching!`,
        duration: 5000
      }));
      
      
    } catch (error) {
      console.error('❌ Error disabling all fields:', error);
      dispatch(addNotification({
        type: 'error',
        message: `❌ Failed to disable all fields: ${error.message}`,
        duration: 5000
      }));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleSaveToDatabase = () => {
    const resultsToSave = documentsState.analysisResults || documentsState.extractionResults;
    
    if (resultsToSave && loanId) {
      // Use the correct save function - savePreviewResults (new system)
      const matchedResults = resultsToSave.match_result || resultsToSave.extraction_result || resultsToSave;
      
      dispatch(savePreviewResults({ matchedResults, loanId })).then((result) => {
        if (result.type.endsWith('/fulfilled')) {
          dispatch(addNotification({
            type: 'success',
            message: `✅ Successfully saved ${result.payload.saved_fields} fields to database!`,
            duration: 5000
          }));
          
          // Refresh the loan data to show the saved results
          if (loanId) {
            // Call the same loadLoanData function that DocumentAnalysis uses
            // This will fetch the extracted results from the database
            window.dispatchEvent(new CustomEvent('refreshLoanData', { 
              detail: { loanId, forceRefresh: true } 
            }));
          }
        } else {
          dispatch(addNotification({
            type: 'error',
            message: '❌ Failed to save data to database. Please try again.',
            duration: 6000
          }));
        }
      }).catch((error) => {
        dispatch(addNotification({
          type: 'error',
          message: '❌ Error saving to database: ' + (error.message || 'Unknown error'),
          duration: 6000
        }));
      });
    } else {
      dispatch(addNotification({
        type: 'warning',
        message: '⚠️ No extraction results to save. Please extract values first.',
        duration: 5000
      }));
    }
  };

  const exportToCSV = () => {
    // Apply the same filtering logic as ResultsGrid
    let filtered = fieldResults;
    
    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(([fieldName, data]) => {
        if (!data) return false; // Skip null/undefined data
        
        return (
          fieldName.toLowerCase().includes(searchLower) ||
          data.value?.toLowerCase().includes(searchLower) ||
          data.master_value?.toLowerCase().includes(searchLower) ||
          data.source?.toLowerCase().includes(searchLower) ||
          data.comment?.toLowerCase().includes(searchLower) ||
          data.document_date?.toLowerCase().includes(searchLower) ||
          data.page?.toString().includes(searchLower) ||
          data.document_match?.toLowerCase().includes(searchLower) ||
          data.system_document_match?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(([, data]) => {
        if (!data) return false; // Skip null/undefined data
        
        switch (activeFilter) {
          case 'high_match':
            return data.system_document_match === 'HIGH' || data.system_document_match === 'PERFECT_MATCH';
          case 'medium_match':
            return data.system_document_match === 'MEDIUM' || data.system_document_match === 'MAYBE_MATCH';
          case 'no_match':
            return data.system_document_match === 'NO_MATCH';
          case 'not_found':
            return data.master_value === 'Not Found' || data.value === 'Not Found';
          case 'both_found':
            return data.master_value !== 'Not Found' && data.value !== 'Not Found';
          case 'missing_master':
            return data.master_value === 'Not Found' && data.value !== 'Not Found';
          case 'discrepancies':
            return data.system_document_match === 'NO_MATCH' && 
                   data.master_value !== 'Not Found' && 
                   data.value !== 'Not Found';
          default:
            return true;
        }
      });
    }

    // Convert to CSV format
    const headers = [
      'Field Name',
      'Document Value',
      'Master Value',
      'Source',
      'Page',
      'Document Date',
      'Document Match',
      'System Match',
      'Overridden',
      'Override Comment',
      'Override Created By'
    ];

    const csvRows = [
      headers.join(','),
      ...filtered.map(([fieldName, data]) => {
        return [
          `"${fieldName}"`,
          `"${data.value || ''}"`,
          `"${data.master_value || ''}"`,
          `"${data.source || ''}"`,
          `"${data.page || ''}"`,
          `"${data.document_date || ''}"`,
          `"${data.document_match || ''}"`,
          `"${data.system_document_match || ''}"`,
          `"${data.overridden ? 'Yes' : 'No'}"`,
          `"${data.override_comment || ''}"`,
          `"${data.override_created_by || ''}"`
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `loan-${loanId || 'fields'}-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Upload history state
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch upload history
  const fetchUploadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch('/system-files/import-logs');
      // apiFetch already returns parsed JSON
      const data = response;
      setUploadHistory(data.logs || []);
    } catch (error) {
      console.error('Error fetching upload history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch upload history when uploads tab is selected
  React.useEffect(() => {
    if (activeSection === 'uploads') {
      fetchUploadHistory();
    }
  }, [activeSection]);


  // Render uploads content




  // Define sections - now always include all action buttons
  const allSections = [
    {
      key: 'case-list',
      title: 'Cases',
      fullTitle: 'Cases',
      icon: BriefcaseMedical,
      color: '#8b5cf6',
      description: 'Review and manage loan cases'
    },
    // {
    //   key: 'tasks',
    //   title: 'Tasks',
    //   fullTitle: 'Task Management',
    //   icon: CheckSquare,
    //   color: '#18C273',
    //   description: 'Assign and track tasks'
    // },
    // {
    //   key: 'activity',
    //   title: 'Activity',
    //   fullTitle: 'Recent Activity',
    //   icon: Zap,
    //   color: '#18C273',
    //   description: 'Real-time updates'
    // },
    {
      key: 'core-data',
      title: 'Core Data',
      fullTitle: 'Core Data',
        icon: Upload,
        color: '#10b981',
      description: 'Navigate to system files'
    },
    {
      key: 'fields',
      title: 'Fields',
      icon: ToolCase,
      color: '#8b5cf6',
      description: 'Configure field settings and mapping rules'
    },
    {
      key: 'admin',
      title: 'Admin',
      fullTitle: 'Administration',
      icon: Shield,
      color: '#dc2626',
      description: 'System administration and settings'
    }
  ];

  // Filter sections based on user role - only show Admin for lead, admin, super_admin
  const sections = allSections.filter(section => {
    if (section.key === 'admin') {
      return user && ['lead', 'admin', 'super_admin'].includes(user.role);
    }
    return true;
  });

  const selectedSection = sections.find(s => s.key === activeSection);

  // Calculate filter counts using exact same logic as ResultsGrid
  const filterCounts = {
    all: fieldResults.length,
    high_match: fieldResults.filter(([, data]) => data && (data.system_document_match === 'HIGH' || data.system_document_match === 'PERFECT_MATCH')).length,
    medium_match: fieldResults.filter(([, data]) => data && (data.system_document_match === 'MEDIUM' || data.system_document_match === 'MAYBE_MATCH')).length,
    no_match: fieldResults.filter(([, data]) => data && data.system_document_match === 'NO_MATCH').length,
    not_found: fieldResults.filter(([, data]) => data && (data.master_value === 'Not Found' || data.value === 'Not Found')).length,
    both_found: fieldResults.filter(([, data]) => data && data.master_value !== 'Not Found' && data.value !== 'Not Found').length,
    missing_master: fieldResults.filter(([, data]) => data && data.master_value === 'Not Found' && data.value !== 'Not Found').length,
    discrepancies: fieldResults.filter(([, data]) => 
      data && data.system_document_match === 'NO_MATCH' && 
      data.master_value !== 'Not Found' && 
      data.value !== 'Not Found'
    ).length
  };
  

  const filters = [
    { key: 'all', label: 'All Results', count: filterCounts.all },
    { key: 'high_match', label: 'High Matches', count: filterCounts.high_match },
    { key: 'medium_match', label: 'Medium Matches', count: filterCounts.medium_match },
    { key: 'no_match', label: 'No Matches', count: filterCounts.no_match },
    { key: 'not_found', label: 'Not Found', count: filterCounts.not_found },
    { key: 'both_found', label: 'Both Found', count: filterCounts.both_found },
    { key: 'missing_master', label: 'Missing Master', count: filterCounts.missing_master },
    { key: 'discrepancies', label: 'Discrepancies', count: filterCounts.discrepancies }
  ];







  
  return (
    <>
      <Sidebar>
        <MenuContent>
          {sections.map((section) => {
            const IconComponent = section.icon;
            const isActive = activeSection === section.key;
            const isDisabled = section.disabled;

            return (
              <SidebarItem
                key={section.key}
                isActive={isActive}
                isDisabled={isDisabled}
                onClick={() => !isDisabled && selectSection(section.key)}
                data-cy={`sidebar-${section.key}`}
              >
                <SidebarIcon isActive={isActive} isDisabled={isDisabled}>
                  <IconComponent size={20} />
                </SidebarIcon>
                <SidebarLabel isDisabled={isDisabled}>{section.title}</SidebarLabel>
              </SidebarItem>
            );
          })}
        </MenuContent>
      </Sidebar>

      <ContentPanel isOpen={isContentPanelOpen}>
        {selectedSection && (
          <>
            <ContentBody>
              {selectedSection.key === 'case-list' ? (
                // Show different content based on whether viewing a specific loan or the list
                (location.pathname.includes('/document-analysis') || location.pathname.includes('/history')) ? (
                  // Route-based panel content for document analysis and history
                  location.pathname.includes('/history') ? (
                    // History page - show same case controls as document analysis
                    <SummaryContent
                    isCreateMode={false}
                    isDocumentAnalysis={true}
                    hasResults={fieldResults.length > 0}
                    showCaseStatusPanel={showCaseStatusPanel}
                    showScansPanel={showScansPanel}
                    setShowScansPanel={setShowScansPanel}
                    onNavigateToScans={() => navigate(`/document-analysis/${loanId}/scans`)}
                    onNavigateToSummary={() => navigate(`/document-analysis/${loanId}`)}
                    onReRunExtraction={() => setShowDocumentSelection(true)}
                    loanId={loanId}
                    renderCaseStatusContent={() => <CaseStatusContent 
                      caseStatus={caseStatus}
                      selectedStatus={selectedStatus}
                      setSelectedStatus={setSelectedStatus}
                      statusComment={statusComment}
                      setStatusComment={setStatusComment}
                      isUpdatingStatus={isUpdatingStatus}
                      setShowCaseStatusPanel={setShowCaseStatusPanel}
                    />}
                    renderScansContent={() => <ScansPanelContent
                      loanId={loanId}
                      onShowDeleteModal={() => setShowDeleteModal(true)}
                      documentsByType={documentsByType}
                      onPreviewDocument={handlePreviewDocument}
                      onToggleMute={handleToggleMute}
                      showAutoPopulate={true}
                      autoPopulateExpanded={autoPopulateExpanded}
                      onToggleAutoPopulate={setAutoPopulateExpanded}
                    />}
                    renderInstructionsContent={() => <InstructionsContent 
                      onReRunExtraction={() => setShowDocumentSelection(true)}
                      loanId={loanId}
                    />}
                    metrics={loanData?.metrics || {}}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    filters={filters}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    caseStatus={caseStatus}
                    setSelectedStatus={setSelectedStatus}
                    setShowCaseStatusPanel={setShowCaseStatusPanel}
                    exportToCSV={exportToCSV}
                    location={location}
                    latestRecords={latestRecords}
                    selectedContracts={selectedContracts}
                    setSelectedContracts={setSelectedContracts}
                    extractionResults={fieldResults}
                    handleSaveToDatabase={handleSaveToDatabase}
                    databaseLoading={databaseLoading}
                    />
                  ) : location.pathname.includes('/scans') ? (
                    // Scans panel content - Instructions panel for upload screen
                    <InstructionsPanelContent
                      loanId={loanId}
                    />
                  ) : location.pathname.includes('/documents') ? (
                    // Documents panel content - Documents panel for tile view
                    <>
                      <ScansPanelContent
                        loanId={loanId}
                        onShowDeleteModal={() => setShowDeleteModal(true)}
                        documentsByType={documentsByType}
                        onPreviewDocument={handlePreviewDocument}
                        onToggleMute={handleToggleMute}
                        showAutoPopulate={true}
                        autoPopulateExpanded={autoPopulateExpanded}
                        onToggleAutoPopulate={setAutoPopulateExpanded}
                      />
                      {/* Delete Confirmation Modal */}
                      <DeleteConfirmationModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                          handlePermanentlyDeleteResults(loanId);
                          setShowDeleteModal(false);
                        }}
                        loanId={loanId}
                        isLoading={documentsState.loading?.deleteResults}
                      />
                    </>
                  ) : (
                    // Default summary content for main document analysis page
                    <SummaryContent
                    isCreateMode={isCreateMode}
                    isDocumentAnalysis={isDocumentAnalysis}
                    hasResults={fieldResults.length > 0}
                    showCaseStatusPanel={showCaseStatusPanel}
                    showScansPanel={showScansPanel}
                    setShowScansPanel={setShowScansPanel}
                    onNavigateToScans={() => navigate(`/document-analysis/${loanId}/scans`)}
                    onNavigateToSummary={() => navigate(`/document-analysis/${loanId}`)}
                    onReRunExtraction={() => setShowDocumentSelection(true)}
                    loanId={loanId}
                    renderCaseStatusContent={() => <CaseStatusContent 
                      caseStatus={caseStatus}
                      selectedStatus={selectedStatus}
                      setSelectedStatus={setSelectedStatus}
                      statusComment={statusComment}
                      setStatusComment={setStatusComment}
                      isUpdatingStatus={isUpdatingStatus}
                      setShowCaseStatusPanel={setShowCaseStatusPanel}
                    />}
                    renderScansContent={() => <DocumentsContent
                      autoPopulateExpanded={autoPopulateExpanded}
                      directoryPath={directoryPath}
                      setDirectoryPath={setDirectoryPath}
                      handleAutoPopulate={handleAutoPopulate}
                      databaseLoading={databaseLoading}
                      databaseErrors={databaseErrors}
                      hasAvailableDocuments={hasAvailableDocuments}
                      documentsByType={documentsByType}
                      handlePreviewDocument={handlePreviewDocument}
                      setAutoPopulateExpanded={setAutoPopulateExpanded}
                      handleAnalyzeDocuments={handleAnalyzeDocuments}
                      handleClearResults={handleClearResults}
                      handlePermanentlyDeleteResults={handlePermanentlyDeleteResults}
                      loading={documentsState.loading}
                      loanId={loanId}
                    />}
                    renderInstructionsContent={() => <InstructionsContent />}
                    metrics={metrics}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    filters={filters}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    caseStatus={caseStatus}
                    setSelectedStatus={setSelectedStatus}
                    setShowCaseStatusPanel={setShowCaseStatusPanel}
                    exportToCSV={exportToCSV}
                    location={location}
                    latestRecords={latestRecords}
                    selectedContracts={selectedContracts}
                    setSelectedContracts={setSelectedContracts}
                    extractionResults={documentsState.extractionResults}
                    handleSaveToDatabase={handleSaveToDatabase}
                    databaseLoading={databaseLoading}
                  />
                  )
                ) : (
                  // Case list panel content
                  <CaseListContent 
                    latestRecords={records.length > 0 ? records : latestRecords}
                    combinedData={loansForTypeahead}
                    loanIdsData={loanIdsForTypeahead}
                    onStatusFilterChange={() => {}} // TODO: Add to Redux
                    onFilteredLoansChange={handleFilteredLoansChange}
                    onSelectedLoansChange={(loans) => {
                      // Convert objects back to strings for Redux
                      const loanIds = loans.map(loan => 
                        typeof loan === 'string' ? loan : loan.loan_id || loan.id
                      );
                      dispatch(setSelectedLoans(loanIds));
                    }}
                    onSelectedBorrowersChange={(borrowers) => {
                      // Convert objects back to strings for Redux
                      const borrowerNames = borrowers.map(borrower => 
                        typeof borrower === 'string' ? borrower : borrower.name || borrower.borrower_name
                      );
                      dispatch(setSelectedBorrowers(borrowerNames));
                    }}
                    statusFilter={'all'} // TODO: Add to Redux
                    selectedLoans={selectedLoans}
                    selectedBorrowers={selectedBorrowers}
                    showSystemValues={showSystemValues}
                    onToggleSystemValues={(value) => dispatch(setShowSystemValues(value))}
                    onShowColumnModal={window.caseListSetShowColumnModal}
                  />
                )
              ) : selectedSection.key === 'core-data' ? (
                // Check if we're on the add-data tab to show upload instructions
                location.search.includes('tab=add-data') ? (
                  <UploadContent onNavigate={setActiveSection} />
                ) : (
                  <SystemFilesContent />
                )
              ) : selectedSection.key === 'fields' ? (
                <FieldsContent
                  showImportSection={showImportSection}
                  showAddFieldSection={showAddFieldSection}
                  defaultDocumentPriority={defaultDocumentPriority}
                  getDocumentKey={getDocumentKey}
                  getDocumentLetter={getDocumentLetter}
                  setShowGlobalPriorityModal={setShowGlobalPriorityModal}
                  setShowImportSection={setShowImportSection}
                  setShowAddFieldSection={setShowAddFieldSection}
                  handleEnableAllFields={handleEnableAllFields}
                  handleDisableAllFields={handleDisableAllFields}
                  bulkUpdating={bulkUpdating}
                  renderImportSection={() => (
                    <ImportSection
                      fieldImportText={fieldImportText}
                      setFieldImportText={setFieldImportText}
                      importingFields={importingFields}
                      setShowImportSection={setShowImportSection}
                      handleImportFields={handleImportFields}
                    />
                  )}
                  renderAddFieldSection={() => (
                    <AddFieldSection
                      newFieldName={newFieldName}
                      setNewFieldName={setNewFieldName}
                      addingField={addingField}
                      setShowAddFieldSection={setShowAddFieldSection}
                      handleAddSimpleField={handleAddSimpleField}
                    />
                  )}
                />
              ) : selectedSection.key === 'activity' ? (
                <ActivityContent />
              ) : selectedSection.key === 'admin' ? (
                <AdminContent
                  statusFilter={window.adminStatusFilter || 'all'}
                  setStatusFilter={(filter) => { window.adminStatusFilter = filter; window.adminApplyFilters?.(); }}
                  assigneeFilter={window.adminAssigneeFilter || 'all'}
                  setAssigneeFilter={(filter) => { window.adminAssigneeFilter = filter; window.adminApplyFilters?.(); }}
                  searchQuery={window.adminSearchQuery || ''}
                  setSearchQuery={(query) => { window.adminSearchQuery = query; window.adminApplyFilters?.(); }}
                  statusCounts={window.adminStatusCounts}
                  assignees={window.adminAssignees}
                  showArchived={window.adminShowArchived || false}
                  setShowArchived={window.adminSetShowArchived}
                />
              ) : (
                  <div style={{ 
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                    {selectedSection?.title || 'Section'}
                  </div>
                  <div>
                    {selectedSection?.description || 'Content coming soon'}
                  </div>
                </div>
              )}
            </ContentBody>
          </>
        )}
      </ContentPanel>
    
    {/* Toggle Button - Outside ContentPanel so it's always visible */}
    {activeSection && (
      <ToggleButton 
        isCollapsed={isManuallyCollapsed}
        onClick={() => {
          setIsManuallyCollapsed(!isManuallyCollapsed);
        }}
      >
        {isManuallyCollapsed ? 
          <ChevronRight size={16} style={{ opacity: 0.2 }} /> : 
          <ChevronLeft size={16} style={{ opacity: 0.2 }} />
        }
      </ToggleButton>
    )}
    
    {/* File Viewer Modal - Rendered outside ActionMenuContainer to avoid z-index stacking context */}
    {showPreview && previewFile && (
      <FileViewer
        document={previewFile}
        documents={loanDocuments}
        isOpen={showPreview}
        onDocumentSelect={setPreviewFile}
        onClose={() => {
          setShowPreview(false);
          setPreviewFile(null);
        }}
      />
    )}
    
    {/* Debug logging for documents */}
    {showPreview && console.log('🔍 [ActionMenu] Passing documents to FileViewer:', {
      totalDocuments: loanDocuments.length,
      documents: loanDocuments.map(doc => ({
        id: doc.id,
        name: doc.name,
        fileName: doc.fileName,
        type: doc.type,
        s3Key: doc.s3Key
      }))
    })}

    {/* Global Document Priority Modal */}
        <DocumentMappingModal
          isOpen={showGlobalPriorityModal}
          field={{
            id: 'global',
            displayName: 'Global Document Priority',
            foundCount: null,
            totalRecords: null,
            completionRate: null
          }}
          onClose={handleGlobalPriorityModalClose}
          onSave={handleGlobalPriorityModalSave}
          isGlobalSettings={true}
        />

        <FieldMatchModal
          isOpen={showMatchModal}
          matches={matchResults?.matches || []}
          newFields={matchResults?.new_fields || []}
          onClose={handleCloseMatchModal}
          onConfirm={handleConfirmMatches}
        />

        {/* Document Selection Modal */}
        {showDocumentSelection && loanId && (
          <DocumentSelectionModal
            isOpen={showDocumentSelection}
            onClose={() => setShowDocumentSelection(false)}
            onConfirm={(selectedDocs) => {
              setSelectedDocumentsForExtraction(selectedDocs);
              setShowDocumentSelection(false);
              setShowExtractionPreview(true);
            }}
            loanId={loanId}
          />
        )}

        {/* Extraction Preview Modal */}
        {showExtractionPreview && loanId && (
          <ExtractionPreview
            loanId={loanId}
            selectedDocuments={selectedDocumentsForExtraction}
            onClose={() => setShowExtractionPreview(false)}
            onApplyOverrides={async (selectedItems) => {
              try {
                console.log('Applying overrides:', selectedItems);
                
                // Save each selected field as an override (value AND match status)
                const savePromises = selectedItems.flatMap(item => {
                  const user_email = user?.email || user?.username || 'system';
                  return [
                    // Save the value override
                    apiFetch('/api/overrides', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        loan_id: loanId,
                        field_uuid: item.field_uuid,  // PRIMARY identifier
                        field_name: item.field_name,  // Kept for backward compatibility
                        override_type: 'value',
                        overridden_value: item.new_source,
                        overridden_key: item.new_source_match || 'OVERRIDE',
                        comment: `Re-run extraction: ${item.existing_source} → ${item.new_source}`,
                        created_by: user_email
                      })
                    }),
                    // Save the match status override to mark as 'perfect' (display status)
                    apiFetch('/api/overrides', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        loan_id: loanId,
                        field_uuid: item.field_uuid,  // PRIMARY identifier
                        field_name: item.field_name,  // Kept for backward compatibility
                        override_type: 'match_status',
                        overridden_value: 'perfect',
                        overridden_key: null,
                        comment: `Override applied - marking as perfect match`,
                        created_by: user_email
                      })
                    })
                  ];
                });
                
                await Promise.all(savePromises);
                
                // Close the preview
                setShowExtractionPreview(false);
                
                // Show success notification
                dispatch(addNotification({
                  type: 'success',
                  message: `✅ Applied ${selectedItems.length} field override${selectedItems.length > 1 ? 's' : ''}`,
                  duration: 3000
                }));
                
                // Refresh the loan data to show the saved results
                window.dispatchEvent(new CustomEvent('refreshLoanData', { 
                  detail: { loanId, forceRefresh: true } 
                }));
                
              } catch (error) {
                console.error('❌ Error saving overrides:', error);
                dispatch(addNotification({
                  type: 'error',
                  message: `❌ Failed to save overrides: ${error.message}`,
                  duration: 5000
                }));
              }
            }}
          />
        )}
    </>
  );
}

export default ActionMenu;
